import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, User, Calendar, Star, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { flagUrl } from "@/lib/countries";
import { fmtUAE, fmtUAEDateOnly, TZ_LABEL } from "@/lib/dates";

export default function PlayerDetailModal({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [matchesById, setMatchesById] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      api.get(`/users/${userId}/profile`),
      api.get(`/matches`),
    ])
      .then(([p, m]) => {
        setData(p.data);
        const map = {};
        m.data.forEach((x) => { map[x.id] = x; });
        setMatchesById(map);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  if (!userId) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] grid place-items-end md:place-items-center p-0 md:p-4 bg-black/70 backdrop-blur-sm"
        data-testid="player-detail-modal"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          className="relative w-full max-w-3xl bg-surface border border-gold/40 md:rounded-3xl rounded-t-3xl p-6 md:p-8 max-h-[92vh] overflow-y-auto shadow-[0_30px_80px_rgba(255,215,0,0.18)]"
        >
          <button
            onClick={onClose}
            data-testid="player-detail-close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full grid place-items-center border border-white/10 text-white/60 hover:border-gold hover:text-gold transition-colors z-10"
          >
            <X size={16}/>
          </button>

          {loading || !data ? (
            <div className="text-center text-white/40 py-20 text-sm tracking-widest uppercase">Loading player…</div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 grid place-items-center shrink-0">
                  <User size={24} className="text-plum"/>
                </div>
                <div>
                  <div className="font-display text-3xl tracking-wider uppercase leading-tight">{data.user.name}</div>
                  <div className="text-lavender text-sm">@{data.user.league_name}{data.user.is_admin && <span className="ml-2 text-[10px] text-gold uppercase tracking-widest">Admin</span>}</div>
                  <div className="text-white/40 text-xs mt-1">Joined {fmtUAEDateOnly(data.user.created_at)} ({TZ_LABEL})</div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                <Stat label="Total" value={data.stats.total_points} accent />
                <Stat label="Match" value={data.stats.match_points} />
                <Stat label="Bonus" value={data.stats.bonus_points} />
                <Stat label="Exact" value={data.stats.exact_count} />
                <Stat label="BTTS Wins" value={data.stats.btts_count} />
              </div>

              {/* Bonus picks */}
              <Section icon={Star} title={`Bonus Picks (${data.bonus_predictions.length}/6)`}>
                {data.bonus_predictions.length === 0 ? (
                  <Empty>No bonus picks yet.</Empty>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {data.bonus_predictions.map((b) => (
                      <div key={b.id} className="bg-black/30 border border-white/5 rounded-xl px-3 py-2 flex items-center justify-between" data-testid={`pl-bonus-${b.type}`}>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-white/40">{b.type.replace(/_/g, " ")}</div>
                          <div className="font-semibold text-sm">{b.value}</div>
                        </div>
                        <span className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full ${b.points > 0 ? "bg-gold/15 text-gold border border-gold/40" : "bg-white/5 text-white/40 border border-white/10"}`}>
                          {b.points > 0 ? `+${b.points}` : "0"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </Section>

              {/* Match predictions */}
              <Section icon={Calendar} title={`Match Predictions (${data.predictions.length})`}>
                {data.predictions.length === 0 ? (
                  <Empty>No match predictions yet.</Empty>
                ) : (
                  <div className="flex flex-col gap-2">
                    {data.predictions.map((p) => {
                      const m = matchesById[p.match_id];
                      if (!m) return null;
                      const isFinished = m.status === "finished";
                      return (
                        <div key={p.id} data-testid={`pl-pred-${p.id}`} className="grid grid-cols-[auto_1fr_auto_1fr_auto] items-center gap-2 px-3 py-2 rounded-lg bg-black/20 border border-white/5 text-sm">
                          <span className="text-[10px] uppercase tracking-widest text-purple">{m.group ? `G${m.group}` : m.stage.replace("Round of ", "R").replace(" ", "").slice(0,4)}</span>
                          <div className="flex items-center gap-2 min-w-0 justify-end">
                            <span className="truncate text-right">{m.home_team}</span>
                            <img src={flagUrl(m.home_code)} className="w-5 h-5 rounded-full ring-1 ring-white/10 object-cover" alt=""/>
                          </div>
                          <span className="font-mono font-bold text-center px-2">
                            {p.home_score}:{p.away_score}
                            <span className="block text-[9px] text-white/40 uppercase tracking-widest">BTTS {p.btts ? "Y" : "N"}</span>
                          </span>
                          <div className="flex items-center gap-2 min-w-0">
                            <img src={flagUrl(m.away_code)} className="w-5 h-5 rounded-full ring-1 ring-white/10 object-cover" alt=""/>
                            <span className="truncate">{m.away_team}</span>
                          </div>
                          <div className="text-right">
                            {isFinished ? (
                              <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${p.points >= 3 ? "bg-gold/15 text-gold border border-gold/40" : p.points >= 1 ? "bg-lavender/15 text-lavender border border-lavender/40" : "bg-white/5 text-white/40 border border-white/10"}`}>
                                +{p.points}
                              </span>
                            ) : (
                              <span className="text-[10px] uppercase tracking-widest text-white/40">pending</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "bg-gold/10 border-gold/40" : "bg-black/30 border-white/5"}`}>
      <div className={`font-mono font-bold text-2xl ${accent ? "text-gold" : ""}`}>{value}</div>
      <div className="text-[9px] tracking-[0.25em] uppercase text-white/40 mt-1">{label}</div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-6">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-2">
        <Icon size={12}/> {title}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="text-center text-white/30 text-sm py-6">{children}</div>;
}
